import { z } from 'zod';
import {
  SESSION_ACTIVITY_APP_VERSION_MAX_LENGTH,
  sessionActivityPlatformSchema,
} from '../api/auth';
import { apiEnvelope } from '../envelope';

// Wire schemas for `POST /api/v1/home/journey-impressions`: the app reports
// which journey practices it actually rendered to the member, one item per
// practice. The server keeps the first sighting per practice per member-local
// day; everything else about the practice (technique, tier, plan) joins
// through its id, so the payload carries no copy and nothing condition-shaped.

/**
 * Where a practice was seen. The single source for the request field and the
 * `journey_practice_impressions.first_surface` CHECK domain.
 */
export const journeyImpressionSurfaceSchema = z.enum(['home', 'guided_conversations_picker']);
export type JourneyImpressionSurface = z.infer<typeof journeyImpressionSurfaceSchema>;

/** Items per request; the home renders at most a handful and the picker a short list. */
export const JOURNEY_IMPRESSIONS_MAX_ITEMS = 20;
/** Highest 0-based rendered position accepted. */
export const JOURNEY_IMPRESSION_MAX_POSITION = 99;

/** A trailing numeric `±hh:mm` offset; `Z` is not accepted. */
const OBSERVED_AT_NUMERIC_OFFSET = /[+-]\d{2}:\d{2}$/;

export const journeyImpressionItemSchema = z
  .object({
    composedPracticeId: z.string().uuid(),
    surface: journeyImpressionSurfaceSchema,
    position: z.number().int().min(0).max(JOURNEY_IMPRESSION_MAX_POSITION),
    /**
     * When the practice was rendered, stamped in the device's zone with its
     * numeric offset. The server reads the member-local day from this offset,
     * so an instant without one, or a `Z` designator (which cannot be told
     * apart from a UTC instant from `toISOString()`), is rejected. A device
     * whose zone is UTC writes `+00:00`.
     */
    observedAt: z
      .string()
      .datetime({ offset: true })
      .regex(OBSERVED_AT_NUMERIC_OFFSET, 'observedAt must end in a numeric ±hh:mm offset'),
  })
  .strict();
export type JourneyImpressionItem = z.infer<typeof journeyImpressionItemSchema>;

/**
 * Strict: the member comes from the bearer token, never from the body, so an
 * unexpected key is rejected rather than ignored.
 */
export const recordJourneyImpressionsRequestSchema = z
  .object({
    appSessionId: z.string().uuid().optional(),
    platform: sessionActivityPlatformSchema,
    appVersion: z.string().min(1).max(SESSION_ACTIVITY_APP_VERSION_MAX_LENGTH).optional(),
    impressions: z.array(journeyImpressionItemSchema).min(1).max(JOURNEY_IMPRESSIONS_MAX_ITEMS),
  })
  .strict();
export type RecordJourneyImpressionsRequest = z.infer<typeof recordJourneyImpressionsRequestSchema>;

/**
 * Counts only: a dropped item is never named back, so the route cannot be used
 * to learn which ids belong to whom. Non-strict, like every client-parsed
 * response.
 */
export const recordJourneyImpressionsDataSchema = z.object({
  recorded: z.number().int().min(0),
  duplicate: z.number().int().min(0),
  dropped: z.number().int().min(0),
});
export type RecordJourneyImpressionsData = z.infer<typeof recordJourneyImpressionsDataSchema>;

export const recordJourneyImpressionsResponseSchema = apiEnvelope(
  recordJourneyImpressionsDataSchema,
);
export type RecordJourneyImpressionsResponse = z.infer<
  typeof recordJourneyImpressionsResponseSchema
>;
